import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import jsPDF from 'jspdf';
import { formatCurrency, generateDetailedPDF } from '../../../utils/reportUtils';
import printerService from '../../../services/printerService';

const ReporteCaja = ({ shiftId }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const getFastFoodBaseURL = () => process.env.REACT_APP_FAST_FOOD_SERVICE || 'http://localhost:8002';

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const response = await api.get(`/api/pos/shifts/${shiftId}/report/`, {
                    baseURL: getFastFoodBaseURL()
                });
                
                const shiftData = response.data;
                const normalizedReport = {
                    ...shiftData.summary,
                    shift_info: shiftData.shift_info,
                    orders_detail: shiftData.orders_detail,
                    payment_methods: shiftData.payment_methods,
                    top_products: shiftData.top_products,
                    expenses: shiftData.expenses || [],
                    date: shiftData.shift_info.opened_at,
                    is_shift_report: true,
                    generated_by: shiftData.shift_info.user
                };
                
                setReportData(normalizedReport);
            } catch (err) {
                console.error("Error fetching shift report:", err);
                setError('No se pudo cargar el reporte del turno.');
            } finally {
                setLoading(false);
            }
        };

        if (shiftId) fetchReport();
    }, [shiftId]);

    if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>Cargando reporte...</div>;
    if (error) return <div style={{ padding: '3rem', textAlign: 'center', color: '#dc3545' }}>{error}</div>;
    if (!reportData) return null;

    const { shift_info } = reportData;
    
    const openingCash = parseFloat(shift_info.opening_cash || 0);
    const closingCash = parseFloat(shift_info.closing_cash || 0); // Este closingCash puede incluir transferencias si fue caja ciega
    const totalSales = parseFloat(reportData.total_sales || 0);
    const totalExpenses = parseFloat(reportData.total_expenses || 0);
    const expensesList = reportData.expenses || [];

    // Desglose de métodos de pago (Sistema)
    let paymentStats = {
        efectivo: 0,
        transferencia: 0,
        tarjeta: 0
    };
    let cancelledStats = { count: 0, total: 0 };
    let employeeStats = { count: 0, total: 0 };
    
    if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            if (['cancelled'].includes(order.status) || order.status_display?.toLowerCase() === 'anulada') {
                cancelledStats.count += 1;
                cancelledStats.total += parseFloat(order.total_amount || order.total || 0);
            } else if (order.notes && order.notes.includes('[VENTA_EMPLEADO]')) {
                employeeStats.count += 1;
                employeeStats.total += parseFloat(order.total_amount || order.total || 0);
            }
        });
    }

    if (reportData.payment_methods && Array.isArray(reportData.payment_methods) && reportData.payment_methods.length > 0) {
        reportData.payment_methods.forEach(pm => {
            const method = String(pm.payment_method__name || pm.method || pm.name || '').toLowerCase();
            const amount = parseFloat(pm.total || pm.amount || 0);
            if (method.includes('efectivo') || method === 'cash') {
                paymentStats.efectivo += amount;
            } else if (method.includes('transferencia') || method.includes('transfer')) {
                paymentStats.transferencia += amount;
            } else if (method.includes('tarjeta') || method.includes('card')) {
                paymentStats.tarjeta += amount;
            }
        });
    } else if (reportData.orders_detail && Array.isArray(reportData.orders_detail)) {
        reportData.orders_detail.forEach(order => {
            if (['delivered', 'completed'].includes(order.status) || order.payment_status === 'paid') {
                const method = String(order.payment_method_display || order.payment_method || '').toLowerCase();
                const total = parseFloat(order.total_amount || order.total || 0);
                if (method.includes('efectivo') || method === 'cash') {
                    paymentStats.efectivo += total;
                } else if (method.includes('transferencia') || method.includes('transfer')) {
                    paymentStats.transferencia += total;
                } else if (method.includes('tarjeta') || method.includes('card')) {
                    paymentStats.tarjeta += total;
                }
            }
        });
    }

    // EXTRAER TRANSFERENCIAS Y TARJETAS FÍSICAS DE LAS NOTAS (Caja Ciega)
    let transferenciasFisico = 0;
    if (shift_info.closing_notes) {
        if (shift_info.closing_notes.includes('Transferencias: $')) {
            const matchT = shift_info.closing_notes.match(/Transferencias:\s*\$?([\d.]+)/);
            if (matchT) {
                transferenciasFisico = parseFloat(matchT[1]);
            }
        }
    }

    const efectivoTotalBruto = openingCash + paymentStats.efectivo;
    const efectivoEsperadoFinal = efectivoTotalBruto;
    
    const efectivoFisicoDeclarado = closingCash - transferenciasFisico; 
    const efectivoFisicoReal = efectivoFisicoDeclarado - totalExpenses;
    
    const sobranteEfectivo = efectivoFisicoReal - efectivoEsperadoFinal;
    
    const transferenciasSistema = paymentStats.transferencia;
    const sobranteTransferencia = transferenciasFisico - transferenciasSistema;

    const handlePrintCaja = async () => {
        const chars_per_line = 42;
        let lines = [];
        
        const center = (text) => {
            const padding = Math.max(0, Math.floor((chars_per_line - text.length) / 2));
            return ' '.repeat(padding) + text;
        };
        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };

        const current_time = new Date();
        const dateStr = current_time.toISOString().split('T')[0];
        const timeStr = current_time.toTimeString().split(' ')[0];

        lines.push(center(`CIERRE DE CAJA #${shift_info.number}`));
        lines.push(center(`${dateStr} ${timeStr}`));
        lines.push(center(`USUARIO: ${shift_info.user || shift_info.manager_name}`));
        lines.push("");

        if (expensesList.length > 0) {
            lines.push(center("GASTOS DE CAJA"));
            expensesList.forEach(exp => {
                const desc = exp.description.length > 25 ? exp.description.substring(0, 25) + ".." : exp.description;
                const idStr = exp.id ? `#${exp.id}: ` : "- ";
                lines.push(rightAlign(`${idStr}${desc}`, `-${parseFloat(exp.amount).toFixed(2)}`));
            });
            lines.push("");
        }

        lines.push("-".repeat(chars_per_line));
        lines.push(center("RESUMEN DEL CIERRE"));
        lines.push("-".repeat(chars_per_line));
        
        // Asumiendo que orders_detail tiene las ventas
        const cantVentas = (reportData.orders_detail || []).filter(o => o.status !== 'cancelled' && o.status_display?.toLowerCase() !== 'anulada').length;
        lines.push(rightAlign(`TOTAL VENTAS     : ${cantVentas}`, `(${totalSales.toFixed(2)})`));
        lines.push(rightAlign(`VENTAS ANULADAS  : ${cancelledStats.count}`, `(${cancelledStats.total.toFixed(2)})`));
        lines.push("=".repeat(chars_per_line));
        
        lines.push(rightAlign("EFECTIVO", paymentStats.efectivo.toFixed(2)));
        lines.push(rightAlign("TRANSFERENCIA", paymentStats.transferencia.toFixed(2)));
        lines.push("=".repeat(chars_per_line));
        
        lines.push(rightAlign(`TOTAL GASTOS      : ${expensesList.length}`, `(-${totalExpenses.toFixed(2)})`));
        lines.push(rightAlign("TOTAL INGRESOS    : 0", "(0.00)"));
        lines.push(rightAlign("TOTAL OTROS/EXTRAS: 0", "(0.00)"));
        lines.push("");

        lines.push(center("-------DESGLOCE TRIBUTARIO VENTAS-------"));
        lines.push(rightAlign(`NOTAS DE ENTREGA  : ${cantVentas}`, `(${totalSales.toFixed(2)})`));
        lines.push(rightAlign("FACTURAS          : 0", "(0.00)"));
        lines.push(rightAlign("      BASE 0% :", "0.00"));
        lines.push(rightAlign("      BASE IVA :", "0.00"));
        lines.push(rightAlign("      IVA :", "0.00"));
        lines.push(rightAlign("      TOTAL :", totalSales.toFixed(2)));
        lines.push("");

        lines.push(center("CAJA"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoEsperadoFinal.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclarado.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivo.toFixed(2)));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferencia.toFixed(2)));
        lines.push("");

        const resSistema = efectivoEsperadoFinal + transferenciasSistema;
        const resFisico = efectivoFisicoDeclarado + transferenciasFisico;
        const resDiferencia = sobranteEfectivo + sobranteTransferencia;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", resSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", resFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", resDiferencia.toFixed(2)));
        lines.push("");

        if (shift_info.closing_notes) {
            // Limpiar y estructurar las notas si vienen del cierre ciego
            let notas = shift_info.closing_notes;
            if (notas.includes("Desglose:")) {
                lines.push(center("Conteo de monedas y billetes:"));
                // Extraer la parte después de "Desglose:"
                let desgloseText = notas.substring(notas.indexOf("Desglose:") + 9).trim();
                // Reemplazar comas por saltos de línea para evitar overflow en impresora térmica
                desgloseText = desgloseText.replace(/, /g, '\n');
                desgloseText.split('\n').forEach(item => {
                    const cleanItem = item.replace('Transferencias:', '').trim();
                    if(cleanItem.length > 0) {
                        lines.push(cleanItem);
                    }
                });
            } else {
                lines.push("NOTAS DE CIERRE:");
                for(let i=0; i<notas.length; i+=chars_per_line) {
                    lines.push(notas.substring(i, i+chars_per_line));
                }
            }
        }

        lines.push("\n\n\n");
        const ticketContent = lines.join("\n");

        try {
            await printerService.printCustomTicket(ticketContent, 'report');
        } catch (error) {
            alert('Error al enviar impresión. Revisa la consola para más detalles.');
        }
    };

    const handleDownloadPDFCaja = () => {
        const chars_per_line = 42;
        let lines = [];
        
        const center = (text) => {
            const padding = Math.max(0, Math.floor((chars_per_line - text.length) / 2));
            return ' '.repeat(padding) + text;
        };
        const rightAlign = (label, value) => {
            const valueStr = String(value);
            const padding = Math.max(0, chars_per_line - label.length - valueStr.length);
            return label + ' '.repeat(padding) + valueStr;
        };

        const current_time = new Date();
        const dateStr = current_time.toISOString().split('T')[0];
        const timeStr = current_time.toTimeString().split(' ')[0];

        lines.push(center(`CIERRE DE CAJA #${shift_info.number}`));
        lines.push(center(`${dateStr} ${timeStr}`));
        lines.push(center(`USUARIO: ${shift_info.user || shift_info.manager_name}`));
        lines.push("");

        if (expensesList.length > 0) {
            lines.push(center("GASTOS DE CAJA"));
            expensesList.forEach(exp => {
                const desc = exp.description.length > 25 ? exp.description.substring(0, 25) + ".." : exp.description;
                const idStr = exp.id ? `#${exp.id}: ` : "- ";
                lines.push(rightAlign(`${idStr}${desc}`, `-${parseFloat(exp.amount).toFixed(2)}`));
            });
            lines.push("");
        }

        lines.push("-".repeat(chars_per_line));
        lines.push(center("RESUMEN DEL CIERRE"));
        lines.push("-".repeat(chars_per_line));
        
        const cantVentas = (reportData.orders_detail || []).filter(o => o.status !== 'cancelled' && o.status_display?.toLowerCase() !== 'anulada').length;
        lines.push(rightAlign(`TOTAL VENTAS     : ${cantVentas}`, `(${totalSales.toFixed(2)})`));
        lines.push(rightAlign(`VENTAS ANULADAS  : ${cancelledStats.count}`, `(${cancelledStats.total.toFixed(2)})`));
        lines.push("=".repeat(chars_per_line));
        
        lines.push(rightAlign("EFECTIVO", paymentStats.efectivo.toFixed(2)));
        lines.push(rightAlign("TRANSFERENCIA", paymentStats.transferencia.toFixed(2)));
        lines.push("=".repeat(chars_per_line));
        
        lines.push(rightAlign(`TOTAL GASTOS      : ${expensesList.length}`, `(-${totalExpenses.toFixed(2)})`));
        lines.push(rightAlign("TOTAL INGRESOS    : 0", "(0.00)"));
        lines.push(rightAlign("TOTAL OTROS/EXTRAS: 0", "(0.00)"));
        lines.push("");

        lines.push(center("-------DESGLOCE TRIBUTARIO VENTAS-------"));
        lines.push(rightAlign(`NOTAS DE ENTREGA  : ${cantVentas}`, `(${totalSales.toFixed(2)})`));
        lines.push(rightAlign("FACTURAS          : 0", "(0.00)"));
        lines.push(rightAlign("      BASE 0% :", "0.00"));
        lines.push(rightAlign("      BASE IVA :", "0.00"));
        lines.push(rightAlign("      IVA :", "0.00"));
        lines.push(rightAlign("      TOTAL :", totalSales.toFixed(2)));
        lines.push("");

        lines.push(center("CAJA"));
        lines.push("-".repeat(chars_per_line));
        
        lines.push("[EFECTIVO]");
        lines.push(rightAlign("SISTEMA", efectivoEsperadoFinal.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", efectivoFisicoDeclarado.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteEfectivo.toFixed(2)));
        lines.push("");

        lines.push("[TRANSFERENCIA]");
        lines.push(rightAlign("SISTEMA", transferenciasSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", transferenciasFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", sobranteTransferencia.toFixed(2)));
        lines.push("");

        const resSistema = efectivoEsperadoFinal + transferenciasSistema;
        const resFisico = efectivoFisicoDeclarado + transferenciasFisico;
        const resDiferencia = sobranteEfectivo + sobranteTransferencia;

        lines.push("RESUMEN:");
        lines.push(rightAlign("SISTEMA", resSistema.toFixed(2)));
        lines.push(rightAlign("CONTEO FISICO", resFisico.toFixed(2)));
        lines.push(rightAlign("DIFERENCIA", resDiferencia.toFixed(2)));
        lines.push("");

        if (shift_info.closing_notes) {
            let notas = shift_info.closing_notes;
            if (notas.includes("Desglose:")) {
                lines.push(center("Conteo de monedas y billetes:"));
                let desgloseText = notas.substring(notas.indexOf("Desglose:") + 9).trim();
                // Reemplazar comas por saltos de línea
                desgloseText = desgloseText.replace(/, /g, '\n');
                desgloseText.split('\n').forEach(item => {
                    const cleanItem = item.replace('Transferencias:', '').trim();
                    if(cleanItem.length > 0) {
                        lines.push(cleanItem);
                    }
                });
            } else {
                lines.push("NOTAS DE CIERRE:");
                for(let i=0; i<notas.length; i+=chars_per_line) {
                    lines.push(notas.substring(i, i+chars_per_line));
                }
            }
        }

        const estHeight = 15 + (lines.length * 4);
        const doc = new jsPDF({ format: [80, estHeight] });
        
        doc.setFont('courier', 'normal');
        doc.setFontSize(8);
        
        let y = 10;
        lines.forEach(line => {
            doc.text(line, 4, y);
            y += 4;
        });

        doc.save(`Cuadre_Caja_${shift_info.number}.pdf`);
    };

    const handlePrintVentas = () => {
        generateDetailedPDF(reportData, 'Reporte de Ventas', `Turno #${shift_info.number}`);
    };

    const styles = {
        container: { fontFamily: 'inherit' },
        headerActions: { display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #e9ecef' },
        titleBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' },
        buttonGroup: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
        titleInfo: { fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e', marginBottom: '0.75rem' },
        textRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', color: '#495057' },
        textTotal: { display: 'flex', justifyContent: 'space-between', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px dashed #ced4da', fontWeight: '700', color: '#1a1a2e' },
        section: { backgroundColor: '#f8f9fa', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' },
        btnPDFCaja: { 
            backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnDownloadPDFCaja: { 
            backgroundColor: '#0d6efd', color: '#fff', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        },
        btnPDFVentas: { 
            backgroundColor: 'var(--secondary-color)', color: 'var(--primary-color)', border: 'none', padding: '0.6rem 1rem', 
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerActions}>
                <div style={styles.titleBar}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>Turno: {shift_info.number}</h2>
                </div>
                <div style={styles.buttonGroup}>
                    <button onClick={handleDownloadPDFCaja} style={styles.btnDownloadPDFCaja}>
                        <i className="bi bi-file-pdf"></i> PDF de Caja
                    </button>
                    <button onClick={handlePrintVentas} style={styles.btnPDFVentas}>
                        <i className="bi bi-file-pdf"></i> PDF de Ventas
                    </button>
                    <button onClick={handlePrintCaja} style={styles.btnPDFCaja}>
                        <i className="bi bi-printer"></i> Imprimir Ticket
                    </button>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Información General</h3>
                <div style={styles.textRow}><span>Encargado:</span> <strong>{shift_info.user || shift_info.manager_name}</strong></div>
                <div style={styles.textRow}><span>Apertura:</span> <span>{new Date(shift_info.opened_at).toLocaleString()}</span></div>
                <div style={styles.textRow}><span>Cierre:</span> <span>{shift_info.closed_at ? new Date(shift_info.closed_at).toLocaleString() : 'En curso'}</span></div>
            </div>

            {/* SECCIÓN DE EFECTIVO */}
            <div style={{ backgroundColor: '#e3f2fd', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#0d47a1' }}>Efectivo</h3>
                <div style={styles.textRow}>
                    <span>Efectivo Inicial:</span> 
                    <span>${openingCash.toFixed(2)}</span>
                </div>
                <div style={styles.textRow}>
                    <span>Ventas del Turno:</span> 
                    <span>${paymentStats.efectivo.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, borderColor: '#0d47a1', color: '#0d47a1', fontSize: '1.1rem' }}>
                    <span>Total Efectivo Esperado:</span> 
                    <span>${efectivoEsperadoFinal.toFixed(2)}</span>
                </div>
                
                <div style={{ ...styles.textRow, marginTop: '1rem' }}>
                    <span>Efec. Declarado (Cajero):</span> 
                    <span>${efectivoFisicoDeclarado.toFixed(2)}</span>
                </div>
                <div style={styles.textRow}>
                    <span>Menos Gastos (Egresos):</span> 
                    <span style={{ color: 'var(--danger-color)' }}>-${totalExpenses.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, borderColor: '#0d47a1', color: '#0d47a1', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <span>Efectivo Físico Real:</span> 
                    <span>${efectivoFisicoReal.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textTotal, border: 'none', color: sobranteEfectivo >= 0 ? 'var(--success-color)' : 'var(--danger-color)', fontSize: '1.1rem', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                    <span>SOBRANTE / FALTANTE EFEC.:</span> 
                    <span>{sobranteEfectivo >= 0 ? '+' : ''}${sobranteEfectivo.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE TRANSFERENCIAS */}
            <div style={{ backgroundColor: '#e8f5e9', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#1b5e20' }}>Transferencias</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Sistema:</span> 
                    <span>${transferenciasSistema.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Transferencias en Físico (Contado):</span> 
                    <span>${transferenciasFisico.toFixed(2)}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: sobranteTransferencia >= 0 ? '#1b5e20' : 'var(--danger-color)' }}>
                    <span>Sobrante / Faltante Transferencias:</span> 
                    <span>{sobranteTransferencia >= 0 ? '+' : ''}${sobranteTransferencia.toFixed(2)}</span>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.titleInfo}>Total General de Ventas (Todos los métodos)</h3>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--primary-color)', textAlign: 'center' }}>
                    ${totalSales.toFixed(2)}
                </div>
            </div>

            {/* SECCIÓN DE VENTAS ANULADAS */}
            <div style={{ backgroundColor: '#fee2e2', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#991b1b' }}>Ventas Anuladas</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas Anuladas:</span> 
                    <span>{cancelledStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#991b1b' }}>
                    <span>Total Ventas Anuladas:</span> 
                    <span>${cancelledStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* SECCIÓN DE VENTAS A EMPLEADOS */}
            <div style={{ backgroundColor: '#e0e7ff', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
                <h3 style={{ ...styles.titleInfo, color: '#3730a3' }}>Ventas a Empleados</h3>
                <div style={{ ...styles.textRow, fontWeight: 'bold' }}>
                    <span>Cantidad de Ventas:</span> 
                    <span>{employeeStats.count}</span>
                </div>
                <div style={{ ...styles.textRow, fontWeight: 'bold', color: '#3730a3' }}>
                    <span>Total Ventas:</span> 
                    <span>${employeeStats.total.toFixed(2)}</span>
                </div>
            </div>

            {/* GASTOS */}
            {expensesList.length > 0 && (
                <div style={styles.section}>
                    <h3 style={styles.titleInfo}>Detalle de Gastos</h3>
                    {expensesList.map(exp => (
                        <div key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #dee2e6' }}>
                            <span style={{ color: '#495057' }}>{exp.description} <br/><small style={{color: '#6c757d'}}>{new Date(exp.created_at).toLocaleTimeString()}</small></span>
                            <span style={{ fontWeight: '600', color: 'var(--danger-color)' }}>${parseFloat(exp.amount).toFixed(2)}</span>
                        </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontWeight: 'bold' }}>
                        <span>TOTAL GASTOS</span>
                        <span style={{ color: 'var(--danger-color)' }}>${totalExpenses.toFixed(2)}</span>
                    </div>
                </div>
            )}
            
            {/* NOTAS */}
            {(shift_info.opening_notes || shift_info.closing_notes) && (
                <div style={{ ...styles.section, backgroundColor: '#fff3cd' }}>
                    <h3 style={{ ...styles.titleInfo, color: '#664d03' }}>Notas</h3>
                    {shift_info.opening_notes && <div style={{ marginBottom: '0.5rem', color: '#664d03' }}><strong>Apertura:</strong> {shift_info.opening_notes}</div>}
                    {shift_info.closing_notes && <div style={{ color: '#664d03', whiteSpace: 'pre-wrap' }}><strong>Cierre:</strong><br/>{shift_info.closing_notes}</div>}
                </div>
            )}
        </div>
    );
};

export default ReporteCaja;
